import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import AlbumBook from '../AlbumBook';
import useBobineSounds from '../../hooks/useBobineSounds';
import { usePhotoSwipe } from '../../hooks/usePhotoSwipe';

const API = `${(process.env.REACT_APP_BACKEND_URL || 'https://bobine.live')}/api`;

/* Fixed-position dropdown that opens UPWARD to avoid covering photos */
const DropdownPortal = ({ buttonTestId, children }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState({ bottom: 0, left: 0 });

  useEffect(() => {
    const updatePos = () => {
      const btn = document.querySelector(`[data-testid="${buttonTestId}"]`);
      if (btn) {
        const r = btn.getBoundingClientRect();
        setPos({ bottom: window.innerHeight - r.top + 4, left: r.left });
      }
    };
    updatePos();
    window.addEventListener('scroll', updatePos, true);
    window.addEventListener('resize', updatePos);
    return () => {
      window.removeEventListener('scroll', updatePos, true);
      window.removeEventListener('resize', updatePos);
    };
  }, [buttonTestId]);

  return (
    <div ref={ref} data-testid="album-picker-dropdown" style={{
      position: 'fixed', bottom: pos.bottom, left: pos.left, zIndex: 99,
      backgroundColor: '#0a0a0a', border: '1px solid rgba(212,175,55,0.25)',
      borderRadius: 10, minWidth: 220, maxHeight: 320, overflowY: 'auto',
      boxShadow: '0 -8px 40px rgba(0,0,0,0.7)',
      scrollbarWidth: 'thin', scrollbarColor: 'rgba(212,175,55,0.15) transparent',
    }}>
      {children}
    </div>
  );
};

const AlbumSection = ({
  albums, setAlbums,
  activeAlbumId, setActiveAlbumId,
  albumPhotos, setAlbumPhotos,
  photoCount,
  token, t,
  openViewer,
}) => {
  const sounds = useBobineSounds();
  const isMobile = window.innerWidth < 768;
  const [showCreateAlbum, setShowCreateAlbum] = useState(false);
  const [newAlbumName, setNewAlbumName] = useState('');
  const [renamingAlbum, setRenamingAlbum] = useState(null);
  const [highlightedAlbumId, setHighlightedAlbumId] = useState(null);
  const [removingPhotoId, setRemovingPhotoId] = useState(null);

  const [showAlbumPicker, setShowAlbumPicker] = useState(false);
  const [albumViewMode, setAlbumViewMode] = useState('grid');
  const [albumViewerIdx, setAlbumViewerIdx] = useState(0);

  // When a NEW album is opened, default to GRID mode (user keeps full control,
  // no photo is auto-displayed; the viewer only opens on explicit photo click)
  useEffect(() => {
    if (activeAlbumId) {
      setAlbumViewerIdx(0);
      setAlbumViewMode('grid');
    }
  }, [activeAlbumId]);

  // Album viewer swipe
  const albumPhotosLen = albumPhotos?.length || 0;
  const isAlbumViewerOpen = !!(activeAlbumId && albumViewMode === 'viewer' && albumPhotosLen > 0);
  const albumSwipe = usePhotoSwipe({
    isOpen: isAlbumViewerOpen,
    onClose: useCallback(() => setAlbumViewMode('grid'), []),
    onPrev: useCallback(() => setAlbumViewerIdx(i => i > 0 ? i - 1 : i), []),
    onNext: useCallback(() => setAlbumViewerIdx(i => i < albumPhotosLen - 1 ? i + 1 : i), [albumPhotosLen]),
    totalPhotos: albumPhotosLen,
    currentIndex: albumViewerIdx,
  });

  // Preload adjacent album photos
  useEffect(() => {
    if (!isAlbumViewerOpen || !albumPhotos) return;
    [-1, 1, 2].forEach(offset => {
      const p = albumPhotos[albumViewerIdx + offset];
      if (p) {
        const t1 = new Image(); t1.src = `${API}/photos/${p.id}/thumbnail?token=${token}`;
        const h1 = new Image(); h1.src = `${API}/photos/${p.id}/image?token=${token}`;
      }
    });
  }, [albumViewerIdx, albumPhotos, isAlbumViewerOpen, token]);

  const createAlbum = async (name) => {
    try {
      const res = await fetch(`${API}/albums/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: name.trim() }),
      });
      const d = await res.json();
      if (d.album) { setAlbums(prev => [d.album, ...prev]); setNewAlbumName(''); setShowCreateAlbum(false); }
    } catch { /* silent */ }
  };

  const renameAlbum = async (albumId, newName) => {
    try {
      await fetch(`${API}/albums/${albumId}/rename`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name: newName }),
      });
      setAlbums(prev => prev.map(a => a.id === albumId ? { ...a, name: newName } : a));
    } catch { /* silent */ }
    setRenamingAlbum(null);
  };

  const deleteAlbum = async (album) => {
    if (!window.confirm(`Supprimer l'album "${album.name}" ?`)) return;
    try {
      await fetch(`${API}/albums/${album.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      setAlbums(prev => prev.filter(a => a.id !== album.id));
      if (activeAlbumId === album.id) { setActiveAlbumId(null); setAlbumPhotos(null); }
    } catch { /* silent */ }
  };

  const changeColor = async (albumId, color) => {
    try {
      await fetch(`${API}/albums/${albumId}/cover-color`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ cover_color: color }),
      });
      setAlbums(prev => prev.map(a => a.id === albumId ? { ...a, cover_color: color } : a));
    } catch {}
  };

  const openAlbum = async (album) => {
    if (activeAlbumId === album.id) {
      setActiveAlbumId(null);
      setAlbumPhotos(null);
      setHighlightedAlbumId(album.id);
      return;
    }
    setActiveAlbumId(album.id);
    try {
      const res = await fetch(`${API}/albums/${album.id}/photos`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json();
      setAlbumPhotos(d.photos || []);
    } catch { setAlbumPhotos([]); }
  };

  const activeAlbum = albums.find(a => a.id === activeAlbumId);

  const [sharingAlbum, setSharingAlbum] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareCustomMessage, setShareCustomMessage] = useState('');
  const [shareGeneratedUrl, setShareGeneratedUrl] = useState('');

  // Reset share modal state when changing album
  useEffect(() => {
    setShowShareModal(false);
    setShareCustomMessage('');
    setShareGeneratedUrl('');
  }, [activeAlbumId]);

  const copyShareUrlSafely = async (text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch { /* fallback below */ }
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.cssText = 'position:fixed;left:-9999px;top:-9999px';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch { return false; }
  };

  const generateShareLink = async () => {
    if (!activeAlbumId) return null;
    if (shareGeneratedUrl) return shareGeneratedUrl;
    try {
      const body = { album_id: activeAlbumId };
      const trimmed = (shareCustomMessage || '').trim();
      if (trimmed) body.message = trimmed;
      const res = await fetch(`${API}/sharing/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) {
        if (res.status === 403) {
          toast.error("Le partage est réservé aux abonnés L'Héritage et Lignées Familiales.");
        } else {
          toast.error(d.detail || "Impossible de créer le lien de partage.");
        }
        return null;
      }
      setShareGeneratedUrl(d.share_url);
      return d.share_url;
    } catch {
      toast.error("Impossible de créer le lien de partage.");
      return null;
    }
  };

  const handleCopyShareLink = async () => {
    if (sharingAlbum) return;
    setSharingAlbum(true);
    try {
      const url = await generateShareLink();
      if (!url) return;
      const copied = await copyShareUrlSafely(url);
      try { sounds.playGoldShimmer && sounds.playGoldShimmer(); } catch {}
      if (copied) {
        toast.success(`Lien copié — « ${activeAlbum?.name || 'Écrin'} » prêt à être partagé.`);
      } else {
        toast.success(`Lien : ${url}`);
      }
    } finally {
      setSharingAlbum(false);
    }
  };

  const handleShareWhatsApp = async () => {
    if (sharingAlbum) return;
    setSharingAlbum(true);
    try {
      const url = await generateShareLink();
      if (!url) return;
      const trimmed = (shareCustomMessage || '').trim();
      const albumLabel = activeAlbum?.name || 'mon écrin';
      const text = trimmed
        ? `${trimmed}\n\n${url}`
        : `J'ai préparé un écrin de souvenirs pour toi sur Bobine — « ${albumLabel} ».\n\n${url}`;
      const waUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
      try { sounds.playGoldShimmer && sounds.playGoldShimmer(); } catch {}
      window.open(waUrl, '_blank', 'noopener,noreferrer');
    } finally {
      setSharingAlbum(false);
    }
  };

  const openShareModal = () => {
    setShareCustomMessage('');
    setShareGeneratedUrl('');
    setShowShareModal(true);
  };
  const closeShareModal = () => setShowShareModal(false);

  const removeFromAlbum = async (photoId) => {
    if (!activeAlbumId) return;
    setRemovingPhotoId(photoId);
    try {
      await fetch(`${API}/albums/${activeAlbumId}/remove-photos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ photo_ids: [photoId] }),
      });
      setAlbumPhotos(prev => prev ? prev.filter(p => p.id !== photoId) : prev);
      setAlbums(prev => prev.map(a => a.id === activeAlbumId ? { ...a, photo_count: Math.max(0, (a.photo_count || 1) - 1) } : a));
    } catch { /* silent */ }
    setRemovingPhotoId(null);
  };

  return (
    <>
      {/* ══════ ALBUMS BAR ══════ */}
      <div data-testid="albums-bar" style={{ marginBottom: 20, position: 'relative' }}>
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: isMobile ? 8 : 16,
          paddingBottom: 60, paddingTop: 4, paddingRight: isMobile ? 12 : 30,
          overflowX: 'auto', overflowY: 'visible', WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none', msOverflowStyle: 'none',
        }}>
          <style>{`[data-testid="albums-bar"] > div::-webkit-scrollbar { display: none; }`}</style>
          {/* "Tous" book with dropdown */}
          <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 6, flexShrink: 0, position: 'relative' }}>
            <button
              data-testid="album-all"
              onClick={() => setShowAlbumPicker(prev => !prev)}
              style={{
                width: isMobile ? 95 : 130, height: isMobile ? 130 : 175, borderRadius: '0 4px 4px 0', cursor: 'pointer',
                backgroundColor: !activeAlbumId ? 'rgba(212,175,55,0.1)' : 'rgba(212,175,55,0.02)',
                border: `1px solid ${!activeAlbumId ? 'rgba(212,175,55,0.35)' : 'rgba(212,175,55,0.08)'}`,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
                transition: 'all 0.3s, transform 0.3s',
                boxShadow: !activeAlbumId ? '0 0 12px rgba(212,175,55,0.06)' : 'none',
              }}
              onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-3px)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="1.2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
              <span style={{ fontFamily: "'Georgia', serif", fontSize: 11, color: '#D4AF37' }}>
                {activeAlbumId ? (albums.find(a => a.id === activeAlbumId)?.name || 'Album') : (t('album_all') || 'Tous')}
              </span>
              <span style={{ fontFamily: "'Georgia', serif", fontSize: 9, color: '#D4AF37' }}>{photoCount} Tr{'\u00e9'}sors</span>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="rgba(212,175,55,0.5)" strokeWidth="2.5"
                style={{ transition: 'transform 0.3s', transform: showAlbumPicker ? 'rotate(180deg)' : 'none' }}>
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>
            {/* Dropdown */}
            {showAlbumPicker && (
              <>
                <div onClick={() => setShowAlbumPicker(false)} style={{ position: 'fixed', inset: 0, zIndex: 98 }} />
                <DropdownPortal buttonTestId="album-all">
                  {/* "Tous" option */}
                  <button
                    data-testid="picker-all"
                    onClick={() => { setActiveAlbumId(null); setAlbumPhotos(null); setHighlightedAlbumId(null); setShowAlbumPicker(false); }}
                    style={{
                      width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10,
                      fontFamily: "'Georgia', serif", fontSize: 13,
                      color: !activeAlbumId ? '#D4AF37' : '#D4AF37',
                      fontWeight: !activeAlbumId ? 700 : 400,
                      background: !activeAlbumId ? 'rgba(212,175,55,0.08)' : 'none',
                      border: 'none', padding: '12px 16px', cursor: 'pointer',
                      borderBottom: '1px solid rgba(212,175,55,0.08)',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={e => { if (activeAlbumId) e.currentTarget.style.background = 'rgba(212,175,55,0.05)'; }}
                    onMouseLeave={e => { if (activeAlbumId) e.currentTarget.style.background = 'none'; }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
                      <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
                    </svg>
                    <span style={{ flex: 1 }}>{t('album_all') || 'Tous'}</span>
                    <span style={{ fontSize: 10, opacity: 0.4 }}>{photoCount}</span>
                  </button>
                  {/* Album list */}
                  {albums.map(a => (
                    <button
                      key={a.id}
                      data-testid={`picker-album-${a.id}`}
                      onClick={() => {
                        setShowAlbumPicker(false);
                        /* Isoler ce livre dans le pave */
                        setHighlightedAlbumId(a.id);
                      }}
                      style={{
                        width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10,
                        fontFamily: "'Georgia', serif", fontSize: 13,
                        color: activeAlbumId === a.id ? '#D4AF37' : '#D4AF37',
                        fontWeight: activeAlbumId === a.id ? 700 : 400,
                        background: activeAlbumId === a.id ? 'rgba(212,175,55,0.08)' : 'none',
                        border: 'none', padding: '11px 16px', cursor: 'pointer',
                        borderBottom: '1px solid rgba(212,175,55,0.05)',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={e => { if (activeAlbumId !== a.id) e.currentTarget.style.background = 'rgba(212,175,55,0.05)'; }}
                      onMouseLeave={e => { if (activeAlbumId !== a.id) e.currentTarget.style.background = 'none'; }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M4 19.5A2.5 2.5 0 016.5 17H20"/>
                        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/>
                      </svg>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.name}</span>
                      <span style={{ fontSize: 10, opacity: 0.35, flexShrink: 0 }}>{a.photo_count || 0}</span>
                    </button>
                  ))}
                </DropdownPortal>
              </>
            )}
          </div>
          {/* Album books — flux en 2 temps: clic 1 = isoler, clic 2 = ouvrir */}
          {(highlightedAlbumId ? albums.filter(a => a.id === highlightedAlbumId) : activeAlbumId ? albums.filter(a => a.id === activeAlbumId) : albums).map(a => (
            <div key={a.id} id={`album-book-${a.id}`} data-testid={`album-book-${a.id}`} style={{ flexShrink: 0, transition: 'all 0.5s ease', position: 'relative' }}>
              <AlbumBook
                album={a} size={isMobile ? "xs" : "sm"}
                isActive={activeAlbumId === a.id}
                token={token}
                onColorChange={(color) => changeColor(a.id, color)}
                onRename={(album) => setRenamingAlbum(album)}
                onDelete={deleteAlbum}
                onClick={() => {
                  if (highlightedAlbumId) setHighlightedAlbumId(null);
                  openAlbum(a);
                }}
              />
              {/* Rename anchored to THIS album */}
              {renamingAlbum && renamingAlbum.id === a.id && (
                <div style={{ position: 'absolute', top: '100%', left: '50%', transform: 'translateX(-50%)', zIndex: 100, marginTop: 6, minWidth: 220, padding: '10px 12px', background: '#0a0a0a', border: '1px solid rgba(212,175,55,0.3)', borderRadius: 10, boxShadow: '0 4px 24px rgba(0,0,0,0.6)', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <input
                    data-testid="album-rename-input"
                    type="text"
                    defaultValue={renamingAlbum.name}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { const n = e.target.value.trim(); if (n) renameAlbum(renamingAlbum.id, n); }
                      if (e.key === 'Escape') setRenamingAlbum(null);
                    }}
                    autoFocus
                    style={{
                      fontFamily: "'Georgia', serif", fontSize: 12,
                      backgroundColor: 'rgba(0,0,0,0.4)', border: '1px solid rgba(212,175,55,0.3)',
                      borderRadius: 6, padding: '6px 12px', color: '#D4AF37', outline: 'none', width: '100%',
                    }}
                  />
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      onClick={() => { const input = document.querySelector('[data-testid="album-rename-input"]'); const n = input?.value?.trim(); if (n) renameAlbum(renamingAlbum.id, n); }}
                      style={{ flex: 1, fontFamily: "'Georgia', serif", fontSize: 11, color: '#000', backgroundColor: 'rgba(212,175,55,0.06)', border: 'none', borderRadius: 4, padding: '5px 8px', cursor: 'pointer' }}>
                      Valider
                    </button>
                    <button
                      onClick={() => { deleteAlbum(renamingAlbum); setRenamingAlbum(null); }}
                      data-testid="album-delete-btn"
                      style={{ fontFamily: "'Georgia', serif", fontSize: 11, color: '#e74c3c', backgroundColor: 'transparent', border: '1px solid rgba(192,57,43,0.2)', borderRadius: 4, padding: '5px 10px', cursor: 'pointer' }}>
                      Supprimer
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {/* Create album button */}
          <div style={{ flexShrink: 0 }}>
            <button
              data-testid="create-album-btn"
              onClick={() => setShowCreateAlbum(!showCreateAlbum)}
              style={{
                width: isMobile ? 95 : 130, height: isMobile ? 130 : 175, borderRadius: '0 4px 4px 0', cursor: 'pointer',
                backgroundColor: 'transparent',
                border: '1px dashed rgba(212,175,55,0.15)',
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10,
                transition: 'all 0.2s, transform 0.3s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#D4AF37'; e.currentTarget.style.transform = 'translateY(-3px)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#D4AF37'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(212,175,55,0.4)" strokeWidth="1.5">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              <span style={{ fontFamily: "'Georgia', serif", fontSize: 10, color: '#D4AF37' }}>
                {t('album_create') || 'Nouvel Album'}
              </span>
            </button>
          </div>
        </div>
        {/* Create album form */}
        {showCreateAlbum && (
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <input
              data-testid="album-name-input"
              type="text"
              placeholder={t('album_name_placeholder') || "Nom de l'album..."}
              value={newAlbumName}
              onChange={e => setNewAlbumName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && newAlbumName.trim()) createAlbum(newAlbumName); }}
              style={{
                flex: 1, fontFamily: "'Georgia', serif", fontSize: 12,
                backgroundColor: 'rgba(0,0,0,0.4)', border: '1px solid rgba(212,175,55,0.2)',
                borderRadius: 6, padding: '8px 12px', color: '#fff', outline: 'none',
              }}
            />
            <button
              data-testid="album-create-confirm"
              onClick={() => { if (newAlbumName.trim()) createAlbum(newAlbumName); }}
              style={{
                fontFamily: "'Georgia', serif", fontSize: 12, color: '#000',
                backgroundColor: 'rgba(212,175,55,0.06)', border: 'none', padding: '8px 16px',
                borderRadius: 6, cursor: 'pointer', fontWeight: 600,
              }}
            >
              {t('album_confirm') || 'Creer'}
            </button>
          </div>
        )}
      </div>

      {/* ══════ ALBUM OUVERT — IMMERSIF ══════ */}
      {activeAlbumId && albumPhotos && (
        <div data-testid="album-expanded-view" style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          backgroundColor: '#050502',
          animation: 'albumZoomIn 0.6s ease-out forwards',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 16px', flexShrink: 0,
            borderBottom: '1px solid rgba(212,175,55,0.1)',
            background: 'linear-gradient(180deg, rgba(5,5,2,1) 0%, rgba(5,5,2,0.95) 100%)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <img src="/bobine-gold-reel.png" alt="Bobine"
                onError={(e) => { e.target.src = '/bobine-reel-transparent.png'; }}
                style={{ width: 32, height: 32, objectFit: 'contain', filter: 'drop-shadow(0 0 6px rgba(212,175,55,0.2))' }} />
              <div>
                <span style={{ fontFamily: "'Georgia', serif", fontSize: 17, color: '#D4AF37', fontWeight: 600, letterSpacing: 0.5 }}>
                  {activeAlbum?.name || 'Album'}
                </span>
                <span style={{ fontFamily: "'Georgia', serif", fontSize: 11, color: '#D4AF37', fontStyle: 'italic', display: 'block', marginTop: 1 }}>
                  {albumPhotos.length} souvenir{albumPhotos.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {/* Toggle Grille / Visionneuse */}
              {albumPhotos.length > 0 && (
                <button
                  data-testid="album-view-toggle"
                  onClick={() => setAlbumViewMode(albumViewMode === 'viewer' ? 'grid' : 'viewer')}
                  style={{
                    fontFamily: "'Georgia', serif", fontSize: 12, cursor: 'pointer',
                    color: '#D4AF37', backgroundColor: 'rgba(212,175,55,0.06)',
                    border: '1px solid rgba(212,175,55,0.2)', borderRadius: 8,
                    padding: '6px 14px', transition: 'all 0.2s',
                    display: 'flex', alignItems: 'center', gap: 6,
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  {albumViewMode === 'viewer' ? (
                    <>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
                      Grille
                    </>
                  ) : (
                    <>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="4" width="20" height="16" rx="2"/><circle cx="12" cy="12" r="3"/></svg>
                      Visionneuse
                    </>
                  )}
                </button>
              )}
              {/* Partager cet écrin */}
              <button
                data-testid="share-album-btn"
                onClick={openShareModal}
                disabled={sharingAlbum}
                title="Partager cet écrin via le Fil de Soie"
                style={{
                  fontFamily: "'Georgia', serif", fontSize: 12,
                  cursor: sharingAlbum ? 'wait' : 'pointer',
                  color: '#0a0a0a', fontWeight: 700, letterSpacing: 0.3,
                  background: 'linear-gradient(145deg, #D4AF37, #B8860B)',
                  border: '1px solid rgba(212,175,55,0.6)',
                  borderRadius: 8,
                  padding: '6px 14px', transition: 'all 0.2s',
                  display: 'flex', alignItems: 'center', gap: 6,
                  boxShadow: '0 1px 3px rgba(212,175,55,0.25), inset 0 1px 0 rgba(255,255,255,0.25)',
                  WebkitTapHighlightColor: 'transparent',
                  opacity: sharingAlbum ? 0.7 : 1,
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="18" cy="5" r="3"/>
                  <circle cx="6" cy="12" r="3"/>
                  <circle cx="18" cy="19" r="3"/>
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                </svg>
                {sharingAlbum ? 'Lien…' : 'Partager'}
              </button>
              {/* Fermer */}
              <button
                data-testid="close-album-view"
                onClick={() => { const closingId = activeAlbumId; setActiveAlbumId(null); setAlbumPhotos(null); setHighlightedAlbumId(closingId); }}
                style={{
                  fontFamily: "'Georgia', serif", fontSize: 12, cursor: 'pointer',
                  color: '#D4AF37', backgroundColor: 'rgba(212,175,55,0.06)',
                  border: '1px solid rgba(212,175,55,0.2)', borderRadius: 8,
                  padding: '6px 14px', transition: 'all 0.2s',
                  display: 'flex', alignItems: 'center', gap: 6,
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M18 6L6 18M6 6l12 12"/>
                </svg>
                Fermer
              </button>
            </div>
          </div>

          {/* Content */}
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
            {albumPhotos.length === 0 ? (
              /* Empty album */
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 20px' }}>
                <div style={{ textAlign: 'center' }}>
                  <img src="/bobine-gold-reel.png" alt="Bobine"
                    onError={(e) => { e.target.src = '/bobine-reel-transparent.png'; }}
                    style={{ width: 140, height: 140, objectFit: 'contain', marginBottom: 28, filter: 'drop-shadow(0 4px 20px rgba(212,175,55,0.2))' }} />
                  <h2 style={{ fontFamily: "'Georgia', serif", fontSize: 28, color: '#D4AF37', fontWeight: 600, letterSpacing: 1, marginBottom: 12 }}>
                    {activeAlbum?.name || 'Album'}
                  </h2>
                  <p style={{ fontFamily: "'Georgia', serif", fontSize: 16, color: '#C5A028', fontStyle: 'italic', opacity: 0.7, marginBottom: 32, letterSpacing: 0.5 }}>
                    Le fil de votre memoire
                  </p>
                  <div style={{ width: 60, height: 1, backgroundColor: 'rgba(212,175,55,0.25)', margin: '0 auto 28px' }} />
                  <p style={{ fontFamily: "'Georgia', serif", fontSize: 14, color: '#D4AF37', lineHeight: 1.6 }}>
                    Ce livre attend vos plus beaux souvenirs
                  </p>
                  <p style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: '#D4AF37', marginTop: 6 }}>
                    Ajoutez des Tr{'\u00e9'}sors depuis votre Coffre-fort
                  </p>
                </div>
              </div>
            ) : albumViewMode === 'viewer' ? (
              /* ── MODE VISIONNEUSE ── */
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
                {/* Navigation arrows */}
                {albumViewerIdx > 0 && (
                  <button data-testid="album-viewer-prev"
                    onClick={() => setAlbumViewerIdx(i => i - 1)}
                    style={{
                      position: 'absolute', left: 6, top: '45%', transform: 'translateY(-50%)',
                      zIndex: 10, background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(212,175,55,0.3)',
                      borderRadius: '50%', width: 40, height: 40, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      WebkitTapHighlightColor: 'transparent',
                    }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6" /></svg>
                  </button>
                )}
                {albumViewerIdx < albumPhotos.length - 1 && (
                  <button data-testid="album-viewer-next"
                    onClick={() => setAlbumViewerIdx(i => i + 1)}
                    style={{
                      position: 'absolute', right: 6, top: '45%', transform: 'translateY(-50%)',
                      zIndex: 10, background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(212,175,55,0.3)',
                      borderRadius: '50%', width: 40, height: 40, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      WebkitTapHighlightColor: 'transparent',
                    }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6" /></svg>
                  </button>
                )}

                {/* Photo display with swipe */}
                <div
                  ref={albumSwipe.photoRef}
                  className={albumSwipe.enterDir}
                  style={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    touchAction: 'none', position: 'relative', overflow: 'hidden',
                  }}
                >
                  {albumPhotos[albumViewerIdx] && (
                    <>
                      <img
                        src={`${API}/photos/${albumPhotos[albumViewerIdx].id}/thumbnail?token=${token}`}
                        alt={albumPhotos[albumViewerIdx].caption || 'Souvenir'}
                        data-testid="album-viewer-photo"
                        draggable={false}
                        style={{
                          maxWidth: '96vw', maxHeight: '72vh', objectFit: 'contain',
                          borderRadius: 4, display: 'block',
                          transform: albumPhotos[albumViewerIdx].rotation ? `rotate(${albumPhotos[albumViewerIdx].rotation}deg)` : 'none',
                        }}
                      />
                      <img
                        key={`album-hd-${albumPhotos[albumViewerIdx].id}`}
                        src={`${API}/photos/${albumPhotos[albumViewerIdx].id}/image?token=${token}`}
                        alt=""
                        draggable={false}
                        onLoad={e => { e.target.style.opacity = '1'; }}
                        style={{
                          position: 'absolute', top: '50%', left: '50%',
                          transform: 'translate(-50%, -50%)',
                          maxWidth: '96vw', maxHeight: '72vh', objectFit: 'contain',
                          borderRadius: 4, opacity: 0,
                          transition: 'opacity 0.4s ease',
                        }}
                      />
                    </>
                  )}
                </div>

                {/* Caption + counter */}
                <div style={{
                  flexShrink: 0, padding: '10px 20px 14px', textAlign: 'center',
                  borderTop: '1px solid rgba(212,175,55,0.08)',
                }}>
                  {albumPhotos[albumViewerIdx]?.caption && (
                    <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: '#D4AF37', fontStyle: 'italic', margin: '0 0 6px' }}>
                      {albumPhotos[albumViewerIdx].caption}
                    </p>
                  )}
                  <span style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: '#D4AF37', letterSpacing: 1 }}>
                    {albumViewerIdx + 1} / {albumPhotos.length}
                  </span>
                </div>
              </div>
            ) : (
              /* ── MODE GRILLE — Livre Hero + photos en dessous ── */
              <div style={{ flex: 1, overflowY: 'auto', padding: '20px 16px 40px' }}>
                {/* Hero — l'Écrin (livre) en grand, le médaillon de couverture, le titre */}
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  textAlign: 'center', padding: '8px 16px 32px',
                  animation: 'albumPhotoFadeIn 0.5s ease-out both',
                }}>
                  <AlbumBook
                    album={{
                      ...(activeAlbum || { id: activeAlbumId, name: '', cover_color: 'noir' }),
                      photo_count: albumPhotos.length,
                    }}
                    size="lg"
                    isActive={false}
                    token={token}
                    onClick={() => {}}
                  />
                  <h2 style={{
                    fontFamily: "'Georgia', serif", fontSize: 26, color: '#D4AF37',
                    fontWeight: 600, letterSpacing: 1, marginTop: 22, marginBottom: 6,
                  }}>
                    {activeAlbum?.name || 'Écrin'}
                  </h2>
                  <p style={{
                    fontFamily: "'Georgia', serif", fontSize: 13, color: '#D4AF37',
                    fontStyle: 'italic', opacity: 0.75, marginBottom: 4,
                  }}>
                    {albumPhotos.length} souvenir{albumPhotos.length !== 1 ? 's' : ''}
                  </p>
                  {!activeAlbum?.cover_photo_id && (
                    <p style={{
                      fontFamily: "'Georgia', serif", fontSize: 11, color: '#D4AF37',
                      opacity: 0.55, marginTop: 10, letterSpacing: 0.3,
                    }}>
                      Cliquez sur l'étoile ★ d'une photo ci-dessous pour la définir comme couverture
                    </p>
                  )}
                  <div style={{ width: 60, height: 1, backgroundColor: 'rgba(212,175,55,0.25)', marginTop: 22 }} />
                </div>

                {/* Grille des photos */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                  {albumPhotos.map((photo, idx) => (
                    <div
                      key={photo.id}
                      data-testid={`album-photo-${photo.id}`}
                      style={{
                        position: 'relative', borderRadius: 8, overflow: 'hidden',
                        border: '1px solid rgba(212,175,55,0.1)',
                        transition: 'transform 0.25s, box-shadow 0.25s',
                        cursor: 'pointer',
                        animation: `albumPhotoFadeIn 0.4s ease-out ${idx * 0.06}s both`,
                      }}
                      onClick={() => { setAlbumViewerIdx(idx); setAlbumViewMode('viewer'); }}
                    >
                      <div style={{ width: '100%', paddingBottom: '100%', position: 'relative', backgroundColor: 'rgba(0,0,0,0.3)' }}>
                        <img
                          src={`${API}/photos/${photo.id}/thumbnail?token=${token}`}
                          alt={photo.caption || ''}
                          loading="lazy"
                          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', transform: photo.rotation ? `rotate(${photo.rotation}deg)` : 'none' }}
                        />
                      </div>
                      {/* Bouton pivoter */}
                      <button
                        data-testid={`rotate-album-photo-${photo.id}`}
                        onClick={async (e) => {
                          e.stopPropagation();
                          const newRot = ((photo.rotation || 0) + 90) % 360;
                          try {
                            await axios.post(`${API}/photos/${photo.id}/rotation`, { rotation: newRot }, { headers: { Authorization: `Bearer ${token}` } });
                            setAlbumPhotos(prev => prev.map(p => p.id === photo.id ? { ...p, rotation: newRot } : p));
                          } catch {}
                        }}
                        style={{
                          position: 'absolute', top: 6, left: 6, zIndex: 5,
                          width: 28, height: 28, borderRadius: '50%', cursor: 'pointer',
                          backgroundColor: 'rgba(0,0,0,0.85)', border: '1.5px solid #D4AF37',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          transition: 'opacity 0.2s', WebkitTapHighlightColor: 'transparent',
                          boxShadow: '0 0 8px rgba(212,175,55,0.4)',
                        }}
                        title="Pivoter"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 4v6h6"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
                        </svg>
                      </button>
                      {/* Bouton "Couverture" — étoile dorée pleine si déjà cover, contour sinon */}
                      <button
                        data-testid={`set-cover-${photo.id}`}
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const res = await fetch(`${API}/albums/${activeAlbumId}/cover-photo`, {
                              method: 'PUT',
                              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                              body: JSON.stringify({ photo_id: photo.id }),
                            });
                            if (!res.ok) throw new Error('http error');
                            setAlbums(prev => prev.map(al => al.id === activeAlbumId ? { ...al, cover_photo_id: photo.id } : al));
                            toast.success('Photo de couverture mise à jour', { style: { fontFamily: "'Georgia', serif", backgroundColor: '#0a0a0a', border: '1px solid rgba(212,175,55,0.3)', color: '#D4AF37' }, duration: 2200 });
                          } catch {
                            toast.error('Échec — réessayez');
                          }
                        }}
                        title={(albums.find(a => a.id === activeAlbumId)?.cover_photo_id === photo.id) ? 'Photo de couverture' : 'Définir comme couverture'}
                        style={{
                          position: 'absolute', top: 6, left: 40, zIndex: 5,
                          width: 28, height: 28, borderRadius: '50%', cursor: 'pointer',
                          backgroundColor: 'rgba(0,0,0,0.85)', border: '1.5px solid #D4AF37',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          transition: 'opacity 0.2s', WebkitTapHighlightColor: 'transparent',
                          boxShadow: '0 0 8px rgba(212,175,55,0.4)',
                        }}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24"
                             fill={(albums.find(a => a.id === activeAlbumId)?.cover_photo_id === photo.id) ? '#D4AF37' : 'none'}
                             stroke="#D4AF37" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                        </svg>
                      </button>
                      {/* Bouton retirer de l'album */}
                      <button
                        data-testid={`remove-album-photo-${photo.id}`}
                        onClick={(e) => { e.stopPropagation(); removeFromAlbum(photo.id); }}
                        disabled={removingPhotoId === photo.id}
                        style={{
                          position: 'absolute', top: 6, right: 6, zIndex: 5,
                          width: 28, height: 28, borderRadius: '50%', cursor: 'pointer',
                          backgroundColor: 'rgba(0,0,0,0.85)', border: '1.5px solid #D4AF37',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          opacity: removingPhotoId === photo.id ? 0.4 : 1,
                          transition: 'opacity 0.2s, background-color 0.2s',
                          WebkitTapHighlightColor: 'transparent',
                          boxShadow: '0 0 8px rgba(212,175,55,0.4)',
                        }}
                        title="Retirer de l'album"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2.5" strokeLinecap="round">
                          <path d="M18 6L6 18M6 6l12 12"/>
                        </svg>
                      </button>
                      {photo.caption && (
                        <div style={{ padding: '8px 10px', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                          <span style={{
                            fontFamily: "'Georgia', serif", fontSize: 11, color: '#FAF3E0',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block',
                          }}>
                            {photo.caption}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* ── Modale "Partager cet écrin" ── */}
          {showShareModal && (
            <div
              data-testid="share-album-modal"
              onClick={closeShareModal}
              style={{
                position: 'fixed', inset: 0, zIndex: 10000,
                backgroundColor: 'rgba(0,0,0,0.78)', backdropFilter: 'blur(6px)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: 20, animation: 'albumZoomIn 0.25s ease-out forwards',
              }}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: '100%', maxWidth: 460,
                  backgroundColor: '#0a0a0a',
                  border: '1px solid rgba(212,175,55,0.35)',
                  borderRadius: 14,
                  boxShadow: '0 30px 80px rgba(0,0,0,0.7), 0 0 30px rgba(212,175,55,0.08)',
                  padding: '28px 24px 22px',
                  fontFamily: "'Georgia', serif",
                }}
              >
                {/* Header avec médaillon de couverture */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', marginBottom: 18 }}>
                  {(() => {
                    const coverId = activeAlbum?.cover_photo_id;
                    const previewPhoto = (coverId && albumPhotos?.find(p => p.id === coverId)) || albumPhotos?.[0];
                    if (previewPhoto) {
                      return (
                        <div style={{
                          width: 96, height: 96, borderRadius: '50%',
                          border: '2px solid rgba(212,175,55,0.6)',
                          boxShadow: '0 0 18px rgba(212,175,55,0.25), inset 0 1px 0 rgba(255,255,255,0.15)',
                          overflow: 'hidden', marginBottom: 14,
                        }}>
                          <img
                            src={`${API}/photos/${previewPhoto.id}/thumbnail?token=${token}`}
                            alt={activeAlbum?.name || 'Écrin'}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        </div>
                      );
                    }
                    return (
                      <div style={{
                        width: 96, height: 96, borderRadius: '50%',
                        border: '2px solid rgba(212,175,55,0.6)',
                        backgroundColor: 'rgba(212,175,55,0.06)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        marginBottom: 14,
                      }}>
                        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="1.4">
                          <rect x="3" y="5" width="18" height="14" rx="2"/>
                          <circle cx="8" cy="11" r="2"/>
                          <path d="M21 15l-5-5L5 21"/>
                        </svg>
                      </div>
                    );
                  })()}
                  <p style={{ fontSize: 11, color: '#D4AF37', letterSpacing: 2, textTransform: 'uppercase', fontWeight: 600, marginBottom: 4 }}>
                    Partager l'écrin
                  </p>
                  <h3 style={{ fontSize: 22, color: '#D4AF37', fontWeight: 600, letterSpacing: 0.5, margin: 0 }}>
                    {activeAlbum?.name || 'Écrin'}
                  </h3>
                  <p style={{ fontSize: 12, color: '#D4AF37', fontStyle: 'italic', marginTop: 4, opacity: 0.7 }}>
                    {(albumPhotos?.length || 0)} souvenir{(albumPhotos?.length || 0) !== 1 ? 's' : ''}
                  </p>
                </div>

                {/* Trait or */}
                <div style={{ width: 44, height: 1, backgroundColor: 'rgba(212,175,55,0.3)', margin: '0 auto 18px' }} />

                {/* Champ message personnalisé */}
                <label style={{ display: 'block', fontSize: 11, color: '#D4AF37', letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 8, fontWeight: 600 }}>
                  Votre message <span style={{ opacity: 0.5, textTransform: 'none', letterSpacing: 0.3, fontWeight: 400 }}>— optionnel</span>
                </label>
                <textarea
                  data-testid="share-custom-message"
                  value={shareCustomMessage}
                  onChange={(e) => { setShareCustomMessage(e.target.value); setShareGeneratedUrl(''); }}
                  placeholder={`Suzanne vous invite à découvrir l'écrin « ${activeAlbum?.name || 'Écrin'} » sur Bobine.`}
                  rows={3}
                  maxLength={400}
                  style={{
                    width: '100%', resize: 'none',
                    backgroundColor: '#050502',
                    border: '1px solid rgba(212,175,55,0.25)',
                    borderRadius: 10, padding: '12px 14px',
                    fontFamily: "'Georgia', serif", fontSize: 14,
                    color: '#F7E6BD', outline: 'none',
                    transition: 'border-color 0.2s',
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'rgba(212,175,55,0.5)'}
                  onBlur={(e) => e.target.style.borderColor = 'rgba(212,175,55,0.25)'}
                />
                <p style={{ fontSize: 11, color: '#D4AF37', opacity: 0.55, marginTop: 6, fontStyle: 'italic' }}>
                  Ce message accompagnera votre lien — laissez vide pour utiliser le message d'accueil par défaut.
                </p>

                {/* Lien généré (si déjà créé) */}
                {shareGeneratedUrl && (
                  <div style={{
                    marginTop: 14, padding: '10px 12px',
                    backgroundColor: 'rgba(212,175,55,0.05)',
                    border: '1px solid rgba(212,175,55,0.2)',
                    borderRadius: 8,
                    fontSize: 12, color: '#D4AF37',
                    wordBreak: 'break-all',
                  }}>
                    {shareGeneratedUrl}
                  </div>
                )}

                {/* Boutons d'action */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 22 }}>
                  <button
                    data-testid="share-copy-link-btn"
                    onClick={handleCopyShareLink}
                    disabled={sharingAlbum}
                    style={{
                      fontFamily: "'Georgia', serif", fontSize: 14,
                      cursor: sharingAlbum ? 'wait' : 'pointer',
                      color: '#0a0a0a', fontWeight: 700, letterSpacing: 0.5,
                      background: 'linear-gradient(145deg, #D4AF37, #B8860B)',
                      border: '1px solid rgba(212,175,55,0.6)',
                      borderRadius: 10, padding: '12px 18px',
                      boxShadow: '0 2px 6px rgba(212,175,55,0.3), inset 0 1px 0 rgba(255,255,255,0.3)',
                      WebkitTapHighlightColor: 'transparent',
                      opacity: sharingAlbum ? 0.7 : 1,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    }}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                    </svg>
                    Copier le lien
                  </button>

                  <button
                    data-testid="share-whatsapp-btn"
                    onClick={handleShareWhatsApp}
                    disabled={sharingAlbum}
                    style={{
                      fontFamily: "'Georgia', serif", fontSize: 14,
                      cursor: sharingAlbum ? 'wait' : 'pointer',
                      color: '#D4AF37', fontWeight: 600, letterSpacing: 0.3,
                      backgroundColor: 'rgba(212,175,55,0.06)',
                      border: '1px solid rgba(212,175,55,0.35)',
                      borderRadius: 10, padding: '12px 18px',
                      WebkitTapHighlightColor: 'transparent',
                      opacity: sharingAlbum ? 0.7 : 1,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    }}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                    </svg>
                    Partager via WhatsApp
                  </button>

                  <button
                    data-testid="share-cancel-btn"
                    onClick={closeShareModal}
                    style={{
                      fontFamily: "'Georgia', serif", fontSize: 13,
                      cursor: 'pointer', color: '#D4AF37',
                      backgroundColor: 'transparent', border: 'none',
                      padding: '6px 0', marginTop: 2,
                      letterSpacing: 0.5,
                      WebkitTapHighlightColor: 'transparent',
                      opacity: 0.7,
                    }}
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default AlbumSection;
